import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import API from "../../api/api";
import { auth } from "../../firebase.config";
import { useUser } from "../../context/UserContext";


declare global {
  interface Window {
    IremboPay: {
      initiate: (options: {
        publicKey: string;
        invoiceNumber: number;
        locale: any;
        callback: (err: any, resp: any) => void;
      }) => void;
      locale: {
        EN: string;
        FR: string;
        RW: string;
      };
      closeModal?: () => void;
    };
  }
}

const GenerateInvoice: React.FC = () => {
  const [billingOption] = useState<"monthly" | "yearly">("yearly");
  const [quantity, setQuantity] = useState<number>(0);
  const [quantityError, setQuantityError] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [currency, setCurrency] = useState<"RWF" | "USD">("USD");
  const { setUser } = useUser();
  const [isPremium] = useState<boolean>(false);
  const {user} = useUser();

  const navigate = useNavigate();
  
  // Convert prices based on currency
  const getUnitPrice = () => {
    if (currency === "RWF") {
      return billingOption === "monthly" ? 400 : 4000;
    } else {
      return 4;
    }
  };

  const unitPrice = getUnitPrice();
  const totalPrice = unitPrice * quantity;

  useEffect(() => {
    // Load the IremboPay script only once
    const script = document.createElement("script");
    script.src = `${process.env.REACT_APP_IREMBO_BASE_URL}`;
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const loadIremboPayScript = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.IremboPay) {
        resolve(); // already loaded
        return;
      }

      const script = document.createElement("script");
      script.src = "https://pay.irembo.com/sdk/irembo-pay.js"; // Replace with correct URL
      script.async = true;
      script.onload = () => {
        if (window.IremboPay) resolve();
        else reject("IremboPay failed to load.");
      };
      script.onerror = () => reject("IremboPay script failed to load.");
      document.body.appendChild(script);
    });
  };

 



  const makePayment = async (invoiceNumber: number) => {
    try {
      await loadIremboPayScript();

      window.IremboPay.initiate({
        publicKey: `${process.env.REACT_APP_PUBLIC_KEY}`,
        invoiceNumber,
        locale: window.IremboPay.locale.EN,
        callback: async (err: any, resp: any) => {
          if (!err) {
            const response = resp as { message: string };

            if (response.message === "Payment successful") {
              window.IremboPay.closeModal?.();
              if (auth.currentUser) {
                await auth.currentUser.reload(); // Refresh the user's ID token
                const updatedUser = auth.currentUser;
                
                // Get the updated ID token
                const token = await updatedUser.getIdToken(true);
                
                // Update local storage
                localStorage.setItem("token", token);
                
                // Update user context with premium status
                setUser({
                  ...user!,
                  isPremium: true,
                });
        
                // Optional: Redirect to dashboard or refresh page
                navigate("/dashboard", { replace: true });
              }
              
            }
          } else {
            console.error("Payment Error", err);
          }
        }
      });

    } catch (error) {
      console.error("Error loading IremboPay:", error);
    }
  };



  const handlePay = async () => {
    setLoading(true);
    setMessage("");

    const customer = {

    };
    try {
      const response = await API.post("/subscription/create_invoice", {
        billingOption,
        quantity,
        customer,
        totalPrice,
        unitPrice,
        currency
      });
      const { invoiceNumber, success } = response.data;
      if (success) {
        makePayment(invoiceNumber);
      }
      setMessage("✅ Invoice created successfully!");
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.error ||
        error.message ||
        "Invoice creation failed";
      setMessage("❌ " + errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container py-4" style={{ padding: "20px", maxWidth: "400px", margin: "0 auto", border: "1px solid #ccc", borderRadius: "8px", marginTop:"2rem" }}>
      <h2 style={{ marginBottom: "15px" }}>Choose Subscription</h2>

      {/* Premium Status Indicator */}
      <div className="mb-4 p-3 border rounded" style={{ backgroundColor: "#f8f9fa" }}>
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h5 className="mb-1">Premium Status</h5>
            <p className="mb-0 text-muted">Current subscription status</p>
          </div>
          <div className="d-flex align-items-center">
            {isPremium ? (
              <span className="badge bg-success p-2" style={{ fontSize: "0.9rem" }}>
                <i className="fas fa-crown me-1"></i>
                Premium Plan
              </span>
            ) : (
              <span className="badge bg-warning text-dark p-2" style={{ fontSize: "0.9rem" }}>
                <i className="fas fa-crown me-1"></i>
                Free Plan
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="d-flex flex-column align-items-start p-3 border rounded shadow-sm" style={{ maxWidth: "400px" }}>
        {/* Currency Selection */}
        <div className="mb-3 w-100">
          <label htmlFor="currency" className="form-label fw-semibold">Select Currency:</label>
          <select
            id="currency"
            className="form-select"
            value={currency}
            onChange={(e) => {
              setCurrency(e.target.value as "RWF" | "USD");
              setQuantity(0);
            }}
          >
            {/* <option value="RWF">Rwandan Franc (RWF)</option> */}
            <option value="USD">US Dollar (USD)</option>
          </select>
        </div>

        {/* Billing Option */}
        <div className="mb-3 w-100">
          <label htmlFor="billingOption" className="form-label fw-semibold">Billing Option:</label>
          <select
            id="billingOption"
            className="form-select"
            value="yearly"
            disabled={true}
          >
            <option value="yearly">Yearly - {currency === "RWF" ? "4000 RWF" : "4.00 USD"}</option>
          </select>
        </div>

        {/* Duration */}
        <div className="mb-3 w-100">
          <label htmlFor="duration" className="form-label fw-semibold">
            Duration (Years):
          </label>
          <input
            id="duration"
            type="number"
            className={`form-control ${quantityError ? 'is-invalid' : ''}`}
            min={1}
            value={quantity}
            onChange={(e) => {
              const inputValue = e.target.value;
              
              // Allow empty input
              if (inputValue === '') {
                setQuantityError("");
                setQuantity(0);
                return;
              }
              
              const value = parseInt(inputValue);
              
              if (isNaN(value)) {
                setQuantityError("Please enter a valid number");
                return;
              }
              
              if (value < 1) {
                setQuantityError("Duration must be at least 1 year");
                return;
              }
              
              setQuantityError("");
              setQuantity(value);
            }}
          />
          {quantityError && <div className="invalid-feedback">{quantityError}</div>}
          <p className="fw-bold mt-2">Total: {totalPrice} {currency}</p>
        </div>

        {/* Pay Button */}
        <button
          onClick={handlePay}
          disabled={loading || totalPrice === 0}
          className={`btn ${loading ? "btn-secondary" : "btn-success"} w-100 ${totalPrice === 0 ? "disabled" : ""}`}
        >
          {loading ? "Processing..." : `Pay ${totalPrice} ${currency}`}
        </button>

        {/* Message */}
        {message && (
          <p className={`mt-3 ${message.startsWith("✅") ? "text-success" : "text-danger"}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
};

export default GenerateInvoice;

